import { useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { useSearchWritersQuery } from '@disco/state-management/api/writers'

const DEBOUNCE_DELAY_MS = 500

interface UseDuplicateWriterSearchOptions {
  /** Current input value to check for duplicates */
  inputValue: string
  /** ID of the writer to exclude from duplicate check (for edit mode) */
  excludeId?: number
}

interface UseDuplicateWriterSearchResult {
  /** Warning message if duplicate found, null otherwise */
  warningMessage: string | null
}

/**
 * Hook for detecting duplicate writer names via debounced search.
 *
 * Used by both CreateWriterModal and EditWriterModal to warn users
 * when a writer with the same name already exists in their contact list.
 *
 * This hook does NOT manage input state - the parent component should manage
 * the input value and pass it to this hook for validation. This pattern is
 * consistent with how PRO and Publisher modals handle duplicate detection.
 *
 * Note: This hook uses debounced API search because Writers can have many entries
 * (potentially thousands) making it impractical to load all into Redux state.
 * PRO and Publisher use synchronous Redux state checks since they have fewer entries.
 * This means Writer duplicate detection has a slight delay compared to PRO/Publisher.
 */
export function useDuplicateWriterSearch({
  inputValue,
  excludeId,
}: UseDuplicateWriterSearchOptions): UseDuplicateWriterSearchResult {
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

  // Use RTK Query to search for writers with matching name (limit 1 since we only need existence check)
  const { data: searchResults = [] } = useSearchWritersQuery(
    { query: debouncedSearchQuery, limit: 1 },
    { skip: !debouncedSearchQuery },
  )

  // Debounce search to avoid excessive API calls while typing
  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    const trimmedValue = value.trim()
    setDebouncedSearchQuery(trimmedValue)
  }, DEBOUNCE_DELAY_MS)

  // Trigger debounced search when inputValue changes
  useEffect(() => {
    debouncedSetSearch(inputValue)
  }, [inputValue, debouncedSetSearch])

  // Only show warning when the search has settled (debouncedSearchQuery matches current input)
  // This prevents showing stale results while the user is still typing
  const inputTrimmedLower = inputValue.trim().toLowerCase()
  const queryTrimmedLower = debouncedSearchQuery.toLowerCase()
  const isSearchSettled = inputTrimmedLower === queryTrimmedLower

  // Check if any search result matches the debounced query exactly (case-insensitive),
  // excluding the specified ID if provided (for edit mode)
  const duplicateWriter = isSearchSettled
    ? searchResults.find(
        writer =>
          writer.name.toLowerCase() === queryTrimmedLower &&
          (excludeId === undefined || writer.id !== excludeId),
      )
    : undefined

  const warningMessage = duplicateWriter
    ? `${duplicateWriter.name} is already in your contact list. Please double-check if this is a duplicate before saving.`
    : null

  return {
    warningMessage,
  }
}
